/**
 * G9. Переезд: «Чемодан». При решении `move.city` / `move.abroad` —
 * восемь вещей, взять четыре: документы, деньги, фото, книга, инструмент,
 * подарок, еда, телефон. Эффект написан прямо на вещи (`effectKey`):
 * без документов — шанс эмиграции −20 % (`flags.abroadChanceDelta`, читает
 * решение move.abroad; в городе — счастье −2 за беготню по справкам [моё]);
 * инструмент — следующий наём уровнем выше с p 0,3 (`flags.jobLevelBonus`);
 * фото — счастье +2 в первый год. Остальные вещи — просто вещи.
 *
 * Собранный чемодан — это «ехать» (`move`/`go`); `input.stay` — остаться.
 * «Пропустить» — автовыбор решения без бонусов и штрафов.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { FLAG_ABROAD_CHANCE_DELTA, FLAG_JOB_LEVEL_BONUS, type Life, type MinigameOutcome, type Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { optionAvailable, pendingOf } from '../skill/common';
import { distinctIndexes, nothing, numberFlag } from './common';

export const SUITCASE_ITEMS = ['documents', 'money', 'photo', 'book', 'instrument', 'gift', 'food', 'phone'] as const;
export type SuitcaseItemId = (typeof SUITCASE_ITEMS)[number];
export const SUITCASE_PICKS = 4;
/** Без документов: поправка к шансу эмиграции. */
export const SUITCASE_DOCS_PENALTY = 0.2;
/** Без документов при переезде в город: счастье. */
export const SUITCASE_NO_DOCS_CITY_HAPPINESS = -2;
/** Инструмент: p, что следующий наём будет уровнем выше. */
export const SUITCASE_INSTRUMENT_P = 0.3;
export const SUITCASE_PHOTO_HAPPINESS = 2;

export interface SuitcaseItem {
  id: SuitcaseItemId;
  key: string;
  /** Ключ надписи об эффекте или null у «просто вещи». */
  effectKey: string | null;
}

export interface SuitcaseState {
  kind: 'move.city' | 'move.abroad';
  items: SuitcaseItem[];
  picks: number;
  /** Вариант «ехать» и «остаться» этого решения. */
  goOption: string;
  stayOption: string;
  timeoutOption: string;
  docsPenalty: number;
  instrumentP: number;
  photoHappiness: number;
}

export type SuitcaseInput = { picks: number[]; stay?: boolean };

const SUITCASE_KINDS = ['move.city', 'move.abroad'] as const;

export function suitcaseItemKey(id: string): string {
  return `minigame.suitcase.item.${id}`;
}

function effectKey(id: SuitcaseItemId, kind: SuitcaseState['kind']): string | null {
  switch (id) {
    case 'documents':
      return kind === 'move.abroad' ? 'minigame.suitcase.effect.documents.abroad' : 'minigame.suitcase.effect.documents.city';
    case 'instrument':
      return 'minigame.suitcase.effect.instrument';
    case 'photo':
      return 'minigame.suitcase.effect.photo';
    default:
      return null;
  }
}

export const suitcase: MinigameDef<SuitcaseInput, SuitcaseState> = {
  id: 'suitcase',
  section: 'G',
  triggers: [
    { kind: 'decision', decision: 'move.city' },
    { kind: 'decision', decision: 'move.abroad' },
  ],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return pendingOf(life, SUITCASE_KINDS) !== null;
  },
  setup(life: Life): SuitcaseState {
    const decision = pendingOf(life, SUITCASE_KINDS);
    const kind = (decision?.kind ?? 'move.city') as SuitcaseState['kind'];
    const goOption = kind === 'move.abroad' ? 'go' : 'move';
    return {
      kind,
      items: SUITCASE_ITEMS.map((id) => ({ id, key: suitcaseItemKey(id), effectKey: effectKey(id, kind) })),
      picks: SUITCASE_PICKS,
      goOption,
      stayOption: 'stay',
      timeoutOption: decision?.timeoutOption ?? 'stay',
      docsPenalty: SUITCASE_DOCS_PENALTY,
      instrumentP: SUITCASE_INSTRUMENT_P,
      photoHappiness: SUITCASE_PHOTO_HAPPINESS,
    };
  },
  play(state: SuitcaseState, input: SuitcaseInput, life: Life, rng: Rng): MinigameOutcome {
    const decision = pendingOf(life, SUITCASE_KINDS);
    const canGo = decision ? optionAvailable(decision, state.goOption) : true;
    if (input.stay === true || !canGo) return { ...nothing(), decisionOption: state.stayOption };
    const picks = distinctIndexes(input.picks, state.items.length).slice(0, state.picks);
    const has = (id: SuitcaseItemId): boolean => picks.some((i) => state.items[i]?.id === id);
    const outcome: MinigameOutcome = { ...nothing(), decisionOption: state.goOption, logKey: has('documents') ? 'minigame.suitcase.packed' : 'minigame.suitcase.noDocuments' };
    const flags: Record<string, number | boolean> = {};
    let happiness = 0;
    if (!has('documents')) {
      if (state.kind === 'move.abroad') flags[FLAG_ABROAD_CHANCE_DELTA] = numberFlag(life, FLAG_ABROAD_CHANCE_DELTA) - state.docsPenalty;
      else happiness += SUITCASE_NO_DOCS_CITY_HAPPINESS;
    }
    if (has('instrument') && rng.chance(state.instrumentP)) flags[FLAG_JOB_LEVEL_BONUS] = 1;
    if (has('photo')) happiness += state.photoHappiness;
    if (Object.keys(flags).length > 0) outcome.flags = flags;
    if (happiness !== 0) outcome.stats = { happiness };
    return outcome;
  },
  expected(state: SuitcaseState): MinigameOutcome {
    return { ...nothing(), decisionOption: state.timeoutOption };
  },
};
